import { adminApi } from "@/redux/_axios";
import {
  AttendanceResponseWithPagination,
  CenterLecture,
  CentersPaginatedResponse,
  CourseParsed,
  CreateResponse,
  EnrollmentsPaginatedResponse,
  LecturesResponseWithPagination,
  ResponseApi,
  ScheduleDetails,
  ScheduleLecturesPaginatedResponse,
  SchedulesPaginatedResponse,
  Student,
  StudentAttendanceDetailsResponseApi,
  TinyLecture,
  TinySchedule,
} from "@/types/center";
import { is } from "date-fns/locale";
import { get } from "http";

export interface AddStudentsToScheduleBody {
  schedule_id: number;
  user_ids: string[];
}
export type ScheduleParams = Partial<{
  center_id: number;
  page: number;
  search: string;
}>;

export interface CreateScheduleBodySave {
  teacher_center_id: number;
  name: string;
}
export type StudentScheduleParams = Partial<{
  search: string;
  schedule_id: number;
  page: number;
}>;
export type ScheduleLecturesParams = Partial<{
  search: string;
  page: number;
}>;
export interface BlockedStudentBody {
  user_id: number;
  block_reason: string;
}

export type TeacherCenterLecturesParamsBase = Partial<{
  per_page: number;
  page: number;
  teacher_id: number;
  center_id: number;
  schedule_id: number;
  search: string;
}>;

export type LecturePayloadSave = Partial<{
  name: string;
  center_course_id: number | null;
  opened_course_id: number | null;
  exam_grade: string;
  prerequisite_lecture_id: number | null;
  prerequisite_course_id: number | null;
  schedule_ids: number[];
}>;

export type TeacherCenterAttendancesParams = Partial<{
  per_page: number;
  page: number;
  schedule_lecture_id: number;
  is_attended: boolean;
  search: string;
  warning: boolean;
  forced: boolean;
  lecture_id: number;
}>;
export interface AddStudentsToAttendanceBody {
  teacher_center_schedule_lecture_id: number;
  user_ids: number[];
  exam_grade: 0;
  is_attended: true;
  warning: true;
  note: "";
  forced: true;
}

export interface CorrectExamBody {
  attendance_id: number;
  exam_grade: number;
}

export interface noteHandlerBody {
  attendance_id: number;
  note?: string;
}

export interface warningHandlerBody {
  attendance_id: number;
}

export type MarkIndividualStudentAttendanceBody = Partial<{
  schedule_lecture_id: number;
  user_id: number;
  is_attended: boolean;
  warning: boolean;
  note: string;
  forced: boolean;
}>;
const centerApiService = {
  getTeacherCenterPagination: ({
    page,
    search,
  }: {
    page: number;
    search: string;
  }) =>
    adminApi.get<CentersPaginatedResponse>(`teacher-centers`, {
      params: { page, search, per_page: 10 },
    }),
  createCenter: (data: { name: string }) =>
    adminApi.post<CreateResponse>(`teacher-centers`, data),
  updateCenter: (id: number, body: { name: string }) =>
    adminApi.put<CreateResponse>(`teacher-centers/${id}`, body),
  deleteCenter: (id: number) =>
    adminApi.delete<CreateResponse>(`teacher-centers/${id}`),

  getSchedule: (schedule_id: number) =>
    adminApi.get<ResponseApi<ScheduleDetails>>(
      `teacher-center-schedules/${schedule_id}`
    ),
  getTeacherCenterSchedules: ({ center_id, page, search }: ScheduleParams) =>
    adminApi.get<SchedulesPaginatedResponse>(`teacher-center-schedules`, {
      params: { per_page: 10, page, center_id, search },
    }),
  createSchedule: (body: CreateScheduleBodySave) =>
    adminApi.post<CreateResponse>(`teacher-center-schedules`, body),
  updateSchedule: (id: number, body: CreateScheduleBodySave) =>
    adminApi.put<CreateResponse>(`teacher-center-schedules/${id}`, body),
  deleteSchedule: (id: number) =>
    adminApi.delete<CreateResponse>(`teacher-center-schedules/${id}`),

  getStudentSchedule: ({ search, schedule_id, page }: StudentScheduleParams) =>
    adminApi.get<EnrollmentsPaginatedResponse>(
      `teacher-center-schedule-students`,
      {
        params: { search, schedule_id, page },
      }
    ),

  getScheduleLectures: ({ search, page }: ScheduleLecturesParams) =>
    adminApi.get<ScheduleLecturesPaginatedResponse>(
      `teacher-center-schedule-lectures`,
      {
        params: { search, page },
      }
    ),

  getStudentsForSelectOption: (search: string) =>
    adminApi.get(
      `teacher-center-schedule-students/eligibleStudents?search=${search}`
    ),
  addStudentsToSchedule: (body: AddStudentsToScheduleBody) =>
    adminApi.post(`teacher-center-schedule-students/bulk-enroll`, body),

  blockedStudent: (body: BlockedStudentBody) =>
    adminApi.post(`teacher-center-schedule-students/handle-block-status`, body),

  getStudentDetails: (
    scheduleId: number,
    userId: number,
    is_attended: number | null
  ) =>
    adminApi.get<StudentAttendanceDetailsResponseApi>(
      `teacher-center-schedules/user-schedule-attendances/${scheduleId}/${userId}`,
      {
        params: { is_attended },
      }
    ),
  getStudentAllDetails: ({
    is_attended,
    userId,
  }: {
    userId?: number;
    is_attended?: boolean;
  }) =>
    adminApi.get<StudentAttendanceDetailsResponseApi>(
      `teacher-center-attendances/without-pagination`,
      {
        params: { is_attended, user_id: userId },
      }
    ),

  getSchedulesWithoutPagination: () =>
    adminApi.get<ResponseApi<TinySchedule[]>>(
      "teacher-center-schedules/without-pagination"
    ),

  getTeacherCenterLectures: ({
    center_id,
    page,
    per_page,
    schedule_id,
    search,
    teacher_id,
  }: TeacherCenterLecturesParamsBase) =>
    adminApi.get<LecturesResponseWithPagination>(`teacher-center-lectures`, {
      params: { center_id, page, per_page, schedule_id, search, teacher_id },
    }),

  getTeacherCenterLecturesForSelect: () =>
    adminApi.get<ResponseApi<TinyLecture[]>>(
      "teacher-center-lectures/without-pagination"
    ),

  getCoursesParsed: () =>
    adminApi.get<ResponseApi<CourseParsed[]>>("courses-parsed"),
  getCenterCoursesParsed: () =>
    adminApi.get<ResponseApi<CourseParsed[]>>("center-courses-parsed"),

  createLecture: (body: LecturePayloadSave) =>
    adminApi.post<CreateResponse>(`teacher-center-lectures`, body),
  updateLecture: ({ body, id }: { body: LecturePayloadSave; id: number }) =>
    adminApi.put(`teacher-center-lectures/${id}`, body),

  getTeacherLectureDetails: (id: number | undefined) =>
    adminApi.get<ResponseApi<CenterLecture>>(`teacher-center-lectures/${id}`),
  getAttendanceLectureWithPagination: ({
    is_attended,
    page,
    per_page,
    schedule_lecture_id,
    search,
    forced,
    lecture_id,
    warning,
  }: TeacherCenterAttendancesParams) =>
    adminApi.get<AttendanceResponseWithPagination>(
      "teacher-center-attendances",
      {
        params: {
          is_attended,
          page,
          per_page,
          schedule_lecture_id,
          search,
          forced,
          lecture_id,
          warning,
        },
      }
    ),
  getTeacherLectureDetailsForSelect: (id?: number) =>
    adminApi.get(`teacher-center-lectures/schedule-lectures/${id}`),
  Recording: (id?: number) =>
    adminApi.post(`teacher-center-attendances/initialize/${id}`),
  markIndividualStudentAttendance: (
    body: MarkIndividualStudentAttendanceBody
  ) => adminApi.post("teacher-center-attendances/mark-attendance", body),
  eligibleStudents: ({
    search,
    lectureId,
    scheduleLectureId,
  }: {
    search?: string;
    lectureId: number;
    scheduleLectureId: number;
  }) =>
    adminApi.get<ResponseApi<Student[]>>(
      `teacher-center-attendances/eligibleStudents/${lectureId}/${scheduleLectureId}`,
      { params: search }
    ),
  correctExam: (body: CorrectExamBody) =>
    adminApi.post("teacher-center-attendances/examCorrection", body),

  noteHandler: (body: noteHandlerBody) =>
    adminApi.post("teacher-center-attendances/update-note", body),
  warningHandler: (body: warningHandlerBody) =>
    adminApi.post("teacher-center-attendances/toggle-warning", body),

  markAllPresent: (schedule_lecture_id: number) =>
    adminApi.post(
      `teacher-center-attendances/mark-all-present/${schedule_lecture_id}`
    ),
  markAllAbsent: (schedule_lecture_id: number) =>
    adminApi.post(
      `teacher-center-attendances/mark-all-absent/${schedule_lecture_id}`
    ),
  getStudentForSelectToPreparePage: ({
    lectureId,
    scheduleLectureId,
    search,
  }: {
    lectureId: number;
    scheduleLectureId: number;
    search?: string;
  }) => {
    return adminApi.get(
      `teacher-center-attendances/eligibleStudents/${lectureId}/${scheduleLectureId}?search=${search}`
    );
  },
  addStudentsToAttendance: (body: AddStudentsToAttendanceBody) => {
    return adminApi.post("teacher-center-attendances", body);
  },
};

export default centerApiService;
